import { DownOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Dropdown, Flex, MenuProps, Modal, Tooltip } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { PatientInfo } from 'lib/api/firebase/apis/apis';
import { AssessmentType } from 'lib/documents/patient-assessment/patient-assessment';

import { ConfirmationState } from './send-assessment-button/components/ConfirmationState';
import { LoadingState } from './send-assessment-button/components/LoadingState';
import { useSendAssessment } from './send-assessment-button/hooks/useSendAssessment';
import { ProgressState } from './send-assessment-button/modal-state-progress';

interface SendAssessmentButtonProps {
  selectedPatients: PatientInfo[];
  onAssessmentsSent: () => void;
}

export function SendAssessmentButton({
  selectedPatients,
  onAssessmentsSent
}: SendAssessmentButtonProps) {
  const {
    isModalVisible,
    assessmentTypeToSend,
    isSending,
    isSentComplete,
    eligiblePatients,
    isQueriesLoading,
    analyzedCount,
    remainingCount,
    isSendBlocked,
    errorList,
    successfulCount,
    failedCount,
    progressRows,
    openModal,
    closeModal,
    handleSendAssessments
  } = useSendAssessment(selectedPatients, onAssessmentsSent);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    openModal(key as AssessmentType);
  };

  const renderModalContent = () => {
    if (isQueriesLoading) {
      return (
        <LoadingState
          analyzedCount={analyzedCount}
          remainingCount={remainingCount}
        />
      );
    }
    if (isSending || isSentComplete) {
      return (
        <ProgressState
          assessmentTypeToSend={assessmentTypeToSend}
          progressRows={progressRows}
          isSentComplete={isSentComplete}
          errorList={errorList}
          successfulCount={successfulCount}
          failedCount={failedCount}
        />
      );
    }
    return (
      <ConfirmationState
        assessmentTypeToSend={assessmentTypeToSend}
        allPatients={selectedPatients}
        eligiblePatients={eligiblePatients}
        progressRows={progressRows}
      />
    );
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'baseline',
      label: 'Send Baseline Assessment'
    },
    {
      key: 'post-injury',
      label: 'Send Post-Injury Assessment'
    }
  ];

  return (
    <>
      <Dropdown
        menu={{ items: menuItems, onClick: handleMenuClick }}
        disabled={selectedPatients.length === 0}
      >
        <BaseButton type="primary">
          Send Assessment <DownOutlined />
        </BaseButton>
      </Dropdown>

      <Modal
        title={
          <Flex gap="small" align="center">
            <ExclamationCircleOutlined style={{ color: '#1890ff' }} />
            <span>Confirm Send Assessment</span>
          </Flex>
        }
        open={isModalVisible}
        onCancel={isSending ? undefined : closeModal}
        closable={!isSending}
        maskClosable={!isSending}
        width={600}
        footer={[
          // Cancel button (only show if not sent complete and not sending)
          !isSentComplete && (
            <BaseButton key="cancel" onClick={closeModal} disabled={isSending}>
              Cancel
            </BaseButton>
          ),
          // OK button
          isSentComplete ? (
            <BaseButton key="ok" type="primary" onClick={closeModal}>
              Close
            </BaseButton>
          ) : (
            (() => {
              const isDisabled = isQueriesLoading || selectedPatients.length === 0 || (eligiblePatients.length > 0 && isSendBlocked);
              const okButton = (
                <BaseButton
                  key="ok"
                  type="primary"
                  loading={isSending}
                  disabled={isDisabled}
                  onClick={handleSendAssessments}
                >
                  {isSending ? 'Sending...' : `Send ${assessmentTypeToSend} Assessment`}
                </BaseButton>
              );

              if (eligiblePatients.length > 0 && isSendBlocked && !isQueriesLoading) {
                return (
                  <Tooltip
                    key="ok-tooltip"
                    title="Your clinic has insufficient assessment balance to send new assessments."
                  >
                    <span>{okButton}</span>
                  </Tooltip>
                );
              }

              return okButton;
            })()
          )
        ]}
      >
        {renderModalContent()}
      </Modal>
    </>
  );
}
